import styled from '@emotion/styled';
import {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Addchart } from '@mui/icons-material';
import { Switch, Skeleton } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { type QuickChartData } from '@trinity/chat';
import { DataTableSettingsButton } from '@trinity/chat-components/components/WidgetGrid/DataTableSettingsButton.js';
import { KPIWidgetSettingsButton } from '@trinity/chat-components/components/WidgetGrid/KPIWidgetSettingsButton.js';
import {
  QuantAnalysisContext,
  QuantAnalysisFilterContext,
} from '@trinity/chat-components';
import { useSelectedQuantAnalysisItem } from '@trinity/chat-components';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { useCookie } from '@trinity/chat-components';
import type {
  QuantAnalysisSortConfig,
  QuantFileTableData,
} from '@trinity/chat/api/interfaces/projects.quant-analysis';
import type { API } from '@trinity/chat';
import { ChartVisualization, type ChartVisualizationRef } from '@/components/Project.QuantAnalysis/components/ChartVisualization';
import { ChartDownload } from '@/components/Project.QuantAnalysis/components/ChartDownload';
import { convertSelectionToFilters, filterTableData, generateChartWithPriority, quickChartingRules, transformChartType, validateSelectionForChart, tryGenerateChartType, createManualSelectionChart } from '@trinity/chat-components/utils/quant-analysis.js';
import { WidgetMapContext } from '@trinity/chat-components/containers/WidgetGrid/context.js';
import { WIDGET_COMPONENT_MAP } from '@/components/WidgetGrid/WidgetsMap';
import { ChartSettings } from '@trinity/chat-components/components/QuantAnalysis/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { CurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context.js';

const SUPPORTED_MANUAL_TYPES: ReadonlySet<QuickChartData['type']> = new Set([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
]);

const summarizePathsForTitle = (paths: string[][], limit = 3): string => {
  if (!paths || paths.length === 0) {
    return '';
  }

  const labels = paths
    .slice(0, limit)
    .map(path => (path.length > 0 ? path.join(' - ') : 'Selection'));

  const summary = labels.join(', ');
  if (paths.length > limit) {
    return `${summary}, ...`;
  }

  return summary;
};

type ValidSelectionContext = {
  invalid: false;
  tableData: QuantFileTableData;
  columnNames: string[];
  rowNames: string[];
  selectedCells?: Map<string, boolean>;
  columnPaths: string[][];
  rowPaths: string[][];
};

type InvalidSelectionContext = {
  invalid: true;
};

type SelectionContext = ValidSelectionContext | InvalidSelectionContext;

const isValidSelectionContext = (
  context: SelectionContext | null
): context is ValidSelectionContext => Boolean(context && !context.invalid);

export const QuantQuickChart = () => {
  const quantFilterContext = useContext(QuantAnalysisFilterContext);
  const [quantFilter, setQuantFilter] = quantFilterContext;
  const quantAnalysis = useContext(QuantAnalysisContext);
  const selectedAnalysisItem = useSelectedQuantAnalysisItem();
  const [selectedChartType, setSelectedChartType] = useState<QuickChartData['type'] | null>(null);
  const appThemingContext = useContext(AppThemingContext);
  const originalTheme = useTheme();
  const title = selectedAnalysisItem?.name;
  const chartRef = useRef<ChartVisualizationRef>(null);

  const cookieName = 'brand-insights:quant-quick-charting-toggle';
  const [cookieValue, setCookie] = useCookie(cookieName);

  // Use cookie value if set, otherwise default to false
  const [showQuickChart, setShowQuickChart] = useState(cookieValue === 'true');

  const color = useMemo(() => {
    return appThemingContext?.enabled && appThemingContext.palette?.primary?.main
      ? appThemingContext?.palette?.primary?.main
      : '#28AAE1';
  }, [appThemingContext?.enabled, appThemingContext?.palette?.primary?.main]);

  const switchTheme = useMemo(() => createTheme({
    palette: {
      primary: {
        main: color,
      },
    },
    fonts: originalTheme.fonts,
  }), [color, originalTheme.fonts]);

  const filteredTableData = useMemo(() => {
    if (!selectedAnalysisItem?.data) return null;

    // Apply custom names to original data before filtering
    let dataWithCustomNames = selectedAnalysisItem.data;
    if (quantFilter.customColumnNames || quantFilter.customRowNames) {
      dataWithCustomNames = {
        ...selectedAnalysisItem.data,
        columnNames: selectedAnalysisItem.data.columnNames.map((col, index) => {
          if (quantFilter.customColumnNames?.has(index)) {
            return quantFilter.customColumnNames.get(index)!;
          }
          return col;
        }),
        rowNames: selectedAnalysisItem.data.rowNames.map((row, index) => {
          if (quantFilter.customRowNames?.has(index)) {
            return quantFilter.customRowNames.get(index)!;
          }
          return row;
        }),
      };
    }

    return filterTableData(
      dataWithCustomNames,
      quantFilter.columnNames,
      quantFilter.rowNames
    );
  }, [selectedAnalysisItem?.data, quantFilter.columnNames, quantFilter.rowNames, quantFilter.customColumnNames, quantFilter.customRowNames]);

  const selectionRowsSize = quantFilter.selection.rows.size;
  const selectionColumnsSize = quantFilter.selection.columns.size;
  const selectionCellsSize = quantFilter.selection.cells.size;

  const hasSelection = useMemo(() =>
    selectionRowsSize > 0 || selectionColumnsSize > 0 || selectionCellsSize > 0,
    [selectionRowsSize, selectionColumnsSize, selectionCellsSize]
  );

  const showChart = hasSelection || quantFilter.columnNames.length > 0 || quantFilter.rowNames.length > 0;

  const sortConfigRef = useRef(quantFilter.sortConfig);

  useEffect(() => {
    sortConfigRef.current = quantFilter.sortConfig;
  }, [quantFilter.sortConfig]);

  const selectionSignature = useMemo(() => {
    const rows = Array.from(quantFilter.selection.rows).sort((a, b) => a - b).join(',');
    const columns = Array.from(quantFilter.selection.columns).sort((a, b) => a - b).join(',');
    const cells = Array.from(quantFilter.selection.cells.keys()).sort().join(',');
    return `${rows}|${columns}|${cells}`;
  }, [quantFilter.selection]);

  const filterSummary = useMemo<API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.FilterSummary | undefined>(() => {
    const hasRowFilters = quantFilter.rowNames.length > 0;
    const hasColumnFilters = quantFilter.columnNames.length > 0;

    if (!hasRowFilters && !hasColumnFilters) {
      return undefined;
    }

    return {
      rowFilters: hasRowFilters ? [...quantFilter.rowNames] : undefined,
      columnFilters: hasColumnFilters ? [...quantFilter.columnNames] : undefined,
    } satisfies API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.FilterSummary;
  }, [quantFilter.rowNames, quantFilter.columnNames]);

  const selectionContext = useMemo<SelectionContext | null>(() => {
    if (!hasSelection || !filteredTableData || !quantFilter.selection) {
      return null;
    }

    if (!validateSelectionForChart(quantFilter.selection)) {
      return { invalid: true };
    }

    const filters = convertSelectionToFilters(
      quantFilter.selection,
      filteredTableData,
      quantFilter.sortConfig ?? null
    );

    return {
      ...filters,
      tableData: filteredTableData,
      invalid: false,
    } satisfies ValidSelectionContext;
  }, [
    hasSelection,
    filteredTableData,
    quantFilter.selection,
    quantFilter.sortConfig,
  ]);


  const derivedTitleHint = useMemo(() => {
    if (isValidSelectionContext(selectionContext)) {
      const rowSummary = summarizePathsForTitle(selectionContext.rowPaths);
      const columnSummary = summarizePathsForTitle(selectionContext.columnPaths);

      if (rowSummary && columnSummary) {
        return `${rowSummary} vs ${columnSummary}`;
      }

      if (rowSummary) {
        return rowSummary;
      }

      if (columnSummary) {
        return columnSummary;
      }

      if (selectionContext.selectedCells && selectionContext.selectedCells.size === 1) {
        const [firstKey] = selectionContext.selectedCells.keys();
        if (firstKey) {
          const dashIndex = firstKey.indexOf('-');
          if (dashIndex !== -1) {
            const rowIndex = Number.parseInt(firstKey.slice(0, dashIndex), 10);
            const colIndex = Number.parseInt(firstKey.slice(dashIndex + 1), 10);

            if (Number.isFinite(rowIndex) && Number.isFinite(colIndex)) {
              const rowPath = selectionContext.tableData.rowNames[rowIndex] ?? [];
              const columnPath = selectionContext.tableData.columnNames[colIndex] ?? [];
              const rawValue = selectionContext.tableData.records?.[rowIndex]?.[colIndex];
              const valueLabel = rawValue != null && rawValue !== '' ? ` (${String(rawValue)})` : '';

              const rowLabel = rowPath.length > 0 ? rowPath.join(' - ') : `Row ${rowIndex + 1}`;
              const columnLabel = columnPath.length > 0 ? columnPath.join(' - ') : `Column ${colIndex + 1}`;

              return `${rowLabel} vs ${columnLabel}${valueLabel}`;
            }
          }
        }
      }
    }

    if (filteredTableData) {
      const rowSummary = summarizePathsForTitle(filteredTableData.rowNames);
      const columnSummary = summarizePathsForTitle(filteredTableData.columnNames);

      if (rowSummary && columnSummary) {
        return `${rowSummary} vs ${columnSummary}`;
      }

      if (rowSummary) {
        return rowSummary;
      }

      if (columnSummary) {
        return columnSummary;
      }
    }

    return title || 'Quantitative Analysis';
  }, [selectionContext, filteredTableData, title]);

  const chartData = useMemo(() => {
    if (!selectedAnalysisItem?.data) {
      return null;
    }

    if (isValidSelectionContext(selectionContext)) {
      const chart = generateChartWithPriority(
        selectionContext.tableData,
        selectionContext.columnNames,
        selectionContext.rowNames,
        sortConfigRef.current,
        derivedTitleHint || 'Quantitative Analysis',
        'var(--gray-xl)',
        selectionContext.selectedCells,
        selectionContext.tableData,
        quantFilter.customColumnNames,
        quantFilter.customRowNames
      );

      if (chart) {
        return chart;
      }

      return quickChartingRules(
        selectionContext.tableData,
        selectionContext.columnNames,
        selectionContext.rowNames,
        sortConfigRef.current,
        quantFilter.customColumnNames,
        quantFilter.customRowNames
      );
    }

    if (selectionContext) {
      return null;
    }

    return quickChartingRules(
      selectedAnalysisItem.data,
      quantFilter.columnNames,
      quantFilter.rowNames,
      sortConfigRef.current,
      quantFilter.customColumnNames,
      quantFilter.customRowNames
    );
  }, [
    selectedAnalysisItem?.data,
    selectionContext,
    quantFilter.columnNames,
    quantFilter.rowNames,
    quantFilter.customColumnNames,
    quantFilter.customRowNames,
    derivedTitleHint,
  ]);

  const apiClient = useChatApiClient();

  const {
    mutate: generateChartTitle,
    reset: resetChartTitleMutation,
    data: generatedChartTitle,
    isPending: isLoadingTitle,
  } = useMutation({
    mutationFn: (data: API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.Request) => {
      return apiClient.quantAnalysis.generateQuantAnalysisChartTitle(data);
    },
    onError: (error) => {
      console.error('Error generating chart title:', error);
    }
  })

  const chartTitle: string = generatedChartTitle?.data?.title ?? derivedTitleHint ?? 'Quantitative Analysis';

  const manualSelectionChart = useMemo<QuickChartData | null>(() => {
    if (!selectedChartType) {
      return null;
    }

    if (!isValidSelectionContext(selectionContext)) {
      return null;
    }

    const { tableData, selectedCells } = selectionContext;

    if (!selectedCells || selectedCells.size === 0) {
      return null;
    }

    if (!SUPPORTED_MANUAL_TYPES.has(selectedChartType)) {
      return null;
    }

    const fallbackTitle = chartTitle || derivedTitleHint || 'Quantitative Analysis';

    return createManualSelectionChart({
      chartType: selectedChartType,
      tableData,
      selectedCells,
      fallbackTitle,
      sortConfig: sortConfigRef.current,
    });
  }, [selectedChartType, selectionContext, chartTitle, derivedTitleHint]);

  const chartTypeForTitle = useMemo<string | null>(() => {
    if (selectedChartType) {
      if (SUPPORTED_MANUAL_TYPES.has(selectedChartType) && manualSelectionChart) {
        return manualSelectionChart.type;
      }

      return selectedChartType;
    }

    if (manualSelectionChart) {
      return manualSelectionChart.type;
    }

    return chartData?.type ?? null;
  }, [selectedChartType, manualSelectionChart, chartData]);

  const columnNamesLength = quantFilter.columnNames.length;
  const rowNamesLength = quantFilter.rowNames.length;
  const columnNamesStr = useMemo(() => quantFilter.columnNames.join(','), [quantFilter.columnNames]);
  const rowNamesStr = useMemo(() => quantFilter.rowNames.join(','), [quantFilter.rowNames]);

  // Generate chart title when filters or selection change
  useEffect(() => {
    if (!quantAnalysis?.projectId || !derivedTitleHint) {
      return;
    }

    if (!chartTypeForTitle) {
      return;
    }

    if (isValidSelectionContext(selectionContext)) {
      if (!selectionSignature) {
        return;
      }

      const hasSelectionData = selectionContext.rowPaths.length > 0 || selectionContext.columnPaths.length > 0;
      if (!hasSelectionData) {
        return;
      }

      let selectionCells: API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.SelectionCell[] | undefined;

      if (selectionContext.selectedCells && selectionContext.selectedCells.size > 0) {
        const cells: API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.SelectionCell[] = [];
        for (const cellKey of selectionContext.selectedCells.keys()) {
          const dashIndex = cellKey.indexOf('-');
          if (dashIndex === -1) {
            continue;
          }

          const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
          const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);

          if (!Number.isFinite(rowIndex) || !Number.isFinite(colIndex)) {
            continue;
          }

          const rowPath = selectionContext.tableData.rowNames[rowIndex] ?? [];
          const columnPath = selectionContext.tableData.columnNames[colIndex] ?? [];

          if (rowPath.length === 0 && columnPath.length === 0) {
            continue;
          }

          const rawValue = selectionContext.tableData.records?.[rowIndex]?.[colIndex];
          const value = typeof rawValue === 'string'
            ? rawValue
            : rawValue != null
              ? String(rawValue)
              : null;

          cells.push({
            rowPath,
            columnPath,
            value,
          });
        }

        if (cells.length > 0) {
          selectionCells = cells;
        }
      }

      const selectionPayload: API.ProjectsQuantAnalysis.GenerateQuantAnalysisChartTitle.SelectionSummary = {
        signature: selectionSignature,
        ...(selectionCells ? { cells: selectionCells } : {}),
      };

      resetChartTitleMutation();
      generateChartTitle({
        projectId: quantAnalysis.projectId,
        title: derivedTitleHint,
        rowPaths: selectionContext.rowPaths,
        columnPaths: selectionContext.columnPaths,
        chartType: chartTypeForTitle,
        filters: filterSummary,
        selection: selectionPayload,
      });
      return;
    }

    if (!filteredTableData) {
      return;
    }

    if (filteredTableData.rowNames.length === 0 && filteredTableData.columnNames.length === 0) {
      return;
    }

    resetChartTitleMutation();
    generateChartTitle({
      projectId: quantAnalysis.projectId,
      title: derivedTitleHint,
      rowPaths: filteredTableData.rowNames,
      columnPaths: filteredTableData.columnNames,
      chartType: chartTypeForTitle,
      filters: filterSummary,
    });
  }, [
    selectionContext,
    selectionSignature,
    filteredTableData,
    quantAnalysis?.projectId,
    generateChartTitle,
    filterSummary,
    chartTypeForTitle,
    derivedTitleHint,
    resetChartTitleMutation,
  ]);

  useEffect(() => {
    setSelectedChartType(null);
  }, [selectionSignature]);

  useEffect(() => {
    if (!hasSelection) {
      setSelectedChartType(null);
      setDataTableCustomizations(prev => ({
        title: prev.title,
        headerColor: prev.headerColor,
        customColumnNames: undefined,
        customRowNames: undefined,
      }));
      setKpiWidgetCustomizations(prev => ({
        title: prev.title,
        backgroundColor: prev.backgroundColor,
        insights: prev.insights,
        values: undefined,
        labels: undefined,
      }));
    }
  }, [hasSelection, columnNamesLength, rowNamesLength, columnNamesStr, rowNamesStr]);

  // Data Table Settings State (must be before currentChart)
  const [dataTableCustomizations, setDataTableCustomizations] = useState<{
    title?: string;
    headerColor?: string;
    customColumnNames?: string[][];
    customRowNames?: string[][];
    sortConfig?: QuantAnalysisSortConfig | null;
  }>({});

  // KPI Widget Settings State
  const [kpiWidgetCustomizations, setKpiWidgetCustomizations] = useState<{
    title?: string;
    backgroundColor?: string;
    values?: string[];
    labels?: string[];
    insights?: string;
  }>({});

  const currentChart = useMemo<QuickChartData | null>(() => {
    if (manualSelectionChart) {
      return manualSelectionChart;
    }

    if (!chartData) return null;

    const baseChart = chartData;

    if (!selectedChartType) {
      return baseChart;
    }

    // Handle data-table conversion
    if (selectedChartType === 'data-table') {
      const sourceData = filteredTableData || selectedAnalysisItem?.data;
      if (!sourceData) return null;

      return {
        type: 'data-table',
        title: dataTableCustomizations.title || chartTitle || derivedTitleHint || 'Data Table',
        headerColor: dataTableCustomizations.headerColor || 'var(--gray-xl)',
        data: sourceData,
        sortConfig: quantFilter?.sortConfig || null,
      } as QuickChartData;
    }

    // Handle kpi-widget conversion
    if (selectedChartType === 'kpi-widget') {
      if (kpiWidgetCustomizations.values && kpiWidgetCustomizations.labels) {
        return {
          type: 'kpi-widget',
          title: kpiWidgetCustomizations.title || chartTitle,
          backgroundColor: kpiWidgetCustomizations.backgroundColor || 'var(--gray-xl)',
          values: kpiWidgetCustomizations.values,
          labels: kpiWidgetCustomizations.labels,
          insights: kpiWidgetCustomizations.insights,
        } as QuickChartData;
      }

      if (isValidSelectionContext(selectionContext)) {
        const selectionDrivenChart = tryGenerateChartType(
          selectionContext.tableData,
          selectionContext.columnNames,
          selectionContext.rowNames,
          sortConfigRef.current,
          'kpi-widget',
          chartTitle || 'Quantitative Analysis',
          'var(--gray-xl)',
          selectionContext.selectedCells,
          selectionContext.tableData,
          quantFilter.customColumnNames,
          quantFilter.customRowNames
        );

        if (selectionDrivenChart && selectionDrivenChart.type === 'kpi-widget') {
          return {
            ...selectionDrivenChart,
            title: kpiWidgetCustomizations.title || selectionDrivenChart.title,
            backgroundColor: kpiWidgetCustomizations.backgroundColor || 'var(--gray-xl)',
            insights: kpiWidgetCustomizations.insights ?? selectionDrivenChart.insights,
          } as QuickChartData;
        }
      }

      const sourceData = filteredTableData || selectedAnalysisItem?.data;
      if (!sourceData) return null;

      const values: string[] = [];
      const labels: string[] = [];
      const maxValues = 6;

      if (sourceData.records && sourceData.records.length > 0 && sourceData.columnNames.length > 0) {
        const records = sourceData.records;
        const rowNames = sourceData.rowNames;
        const columnNames = sourceData.columnNames;
        const recordsLen = records.length;
        const columnNamesLen = columnNames.length;

        for (let rowIndex = 0; rowIndex < recordsLen && values.length < maxValues; rowIndex++) {
          const row = records[rowIndex];
          if (!row) continue;

          const rowLen = row.length;
          for (let colIndex = 0; colIndex < rowLen && values.length < maxValues; colIndex++) {
            values.push(String(row[colIndex] ?? ''));

            const rowName = rowNames[rowIndex];
            const rowLabel = Array.isArray(rowName)
              ? rowName.join(' - ')
              : String(rowName || `Row ${rowIndex + 1}`);

            const colName = colIndex < columnNamesLen ? columnNames[colIndex] : undefined;
            const colLabel = Array.isArray(colName)
              ? colName.join(' - ')
              : String(colName || `Col ${colIndex + 1}`);

            labels.push(`${rowLabel} - ${colLabel}`);
          }
        }
      }

      return {
        type: 'kpi-widget',
        title: kpiWidgetCustomizations.title || chartTitle,
        backgroundColor: kpiWidgetCustomizations.backgroundColor || 'var(--gray-xl)',
        values,
        labels,
        insights: kpiWidgetCustomizations.insights,
      } as QuickChartData;
    }

    if (baseChart.type === 'multiple-clustered-bar' && selectedChartType === 'multiple-stacked-bar') {
      return {
        ...baseChart,
        type: 'multiple-stacked-bar' as const,
      } as QuickChartData;
    }

    if (baseChart.type === 'multiple-stacked-bar' && selectedChartType === 'multiple-clustered-bar') {
      return {
        ...baseChart,
        type: 'multiple-clustered-bar' as const,
      } as QuickChartData;
    }

    return transformChartType(baseChart, selectedChartType);
  }, [chartData, selectedChartType, chartTitle, derivedTitleHint, filteredTableData, selectedAnalysisItem?.data, dataTableCustomizations.title, dataTableCustomizations.headerColor, dataTableCustomizations.customColumnNames, dataTableCustomizations.customRowNames, quantFilter?.sortConfig, quantFilter.customColumnNames, quantFilter.customRowNames, selectionContext, kpiWidgetCustomizations.title, kpiWidgetCustomizations.backgroundColor, kpiWidgetCustomizations.values, kpiWidgetCustomizations.labels, kpiWidgetCustomizations.insights]);

  const handleChartChange = useCallback((newType: QuickChartData['type']) => {
    setSelectedChartType(newType);
  }, []);

  const handleDataTableSettingsSave = useCallback(
    (updatedChart: QuickChartData & { type: 'data-table' }) => {
      // Store the customizations
      setDataTableCustomizations({
        title: updatedChart.title,
        headerColor: updatedChart.headerColor,
        customColumnNames: updatedChart.customColumnNames,
        customRowNames: updatedChart.customRowNames,
      });
      // Force re-render by updating selected chart type
      setSelectedChartType('data-table');
    },
    []
  );

  const handleKpiWidgetSettingsSave = useCallback(
    (updatedChart: QuickChartData & { type: 'kpi-widget' }) => {
      // Store the customizations
      setKpiWidgetCustomizations({
        title: updatedChart.title,
        backgroundColor: updatedChart.backgroundColor,
        values: updatedChart.values,
        labels: updatedChart.labels,
        insights: updatedChart.insights,
      });
      // Force re-render by updating selected chart type
      setSelectedChartType('kpi-widget');
    },
    []
  );

  const toggleQuickChart = () => {
    const newValue = !showQuickChart;
    setShowQuickChart(newValue);
    setCookie({
      expires: new Date('2100-01-01'),
      value: newValue ? 'true' : 'false',
    });
  };

  const chartSaveEnabled = showQuickChart && showChart && currentChart;
  const originalChartType = chartData?.type;
  const selectedCellCount = quantFilter.selection?.cells?.size ?? 0;

  // Calculate selection dimensions for chart type determination
  const selectionDimensions = useMemo(() => {
    if (!isValidSelectionContext(selectionContext)) {
      return { rowCount: 0, columnCount: 0 };
    }

    const uniqueRows = new Set<number>();
    const uniqueColumns = new Set<number>();

    if (selectionContext.selectedCells) {
      for (const cellKey of selectionContext.selectedCells.keys()) {
        const dashIndex = cellKey.indexOf('-');
        if (dashIndex !== -1) {
          const rowIndex = Number.parseInt(cellKey.slice(0, dashIndex), 10);
          const colIndex = Number.parseInt(cellKey.slice(dashIndex + 1), 10);
          if (Number.isFinite(rowIndex) && Number.isFinite(colIndex)) {
            uniqueRows.add(rowIndex);
            uniqueColumns.add(colIndex);
          }
        }
      }
    }

    return { rowCount: uniqueRows.size, columnCount: uniqueColumns.size };
  }, [selectionContext]);

  return (
    <CurrentSegmentIdsContext.Provider value={{ projectId: quantAnalysis.projectId, segmentId: null, projectParentId: null, slug: null }}>
      <WidgetMapContext.Provider value={WIDGET_COMPONENT_MAP}>
        <BreakLine />
        <ToggleSection>
          <ToggleContainer>
            <ThemeProvider theme={switchTheme}>
              <Switch
                checked={showQuickChart}
                onChange={toggleQuickChart}
                color="primary"
              />
            </ThemeProvider>
            <ToggleLabel>Chart Data</ToggleLabel>
          </ToggleContainer>

          <ToggleContainerRight>
            {/* <ChartSave disabled={!chartSaveEnabled} currentChart={currentChart} chartTitle={chartTitle} /> */}
          </ToggleContainerRight>

        </ToggleSection>
        <BreakLine />

        {showQuickChart && (
          <Root>
            {showChart && currentChart ? (
              <>
                <ChartHeader>
                  {isLoadingTitle ? (
                    <Skeleton width={300} height={28} variant="text" />
                  ) : (
                    <ChartTitle>
                      {(currentChart.type === 'data-table' &&
                        currentChart.title) ||
                        (currentChart.type === 'kpi-widget' && currentChart.title)
                        ? currentChart.title
                        : chartTitle}
                    </ChartTitle>
                  )}
                  <ChartControls>
                    <DataTableSettingsButton
                      currentChart={currentChart}
                      onSettingsSave={handleDataTableSettingsSave} />
                    <KPIWidgetSettingsButton
                      currentChart={currentChart}
                      onSettingsSave={handleKpiWidgetSettingsSave} />
                    <ChartDownload
                      chartData={currentChart}
                      title={chartTitle}
                      imageExportRef={chartRef} />
                    <ChartSettings
                      chartData={currentChart}
                      originalChartType={originalChartType}
                      selectedCellCount={selectedCellCount}
                      selectedRowCount={selectionDimensions.rowCount}
                      selectedColumnCount={selectionDimensions.columnCount}
                      onChartTypeChange={handleChartChange}
                    />
                  </ChartControls>
                </ChartHeader>
                <ChartContainer>
                  <ChartVisualization
                    ref={chartRef}
                    chart={currentChart} />
                </ChartContainer>
              </>
            ) : (
              <EmptyState>
                <StyledAddChart />
                <div>No chart available</div>
              </EmptyState>
            )}
          </Root>
        )}
      </WidgetMapContext.Provider>
    </CurrentSegmentIdsContext.Provider>
  );
};

const Root = styled.div`
  border: 1px solid var(--gray-l);
  border-radius: 15px;
  padding: 12px;
  background-color: white;
  margin-bottom: 26px;
`;

const BreakLine = styled.hr`
  border: none;
  height: 1px;
  background-color: var(--gray-l);
  margin: 8px 0;
`;

const ToggleSection = styled.div`
  display: flex;
  padding: 8px 0;
`;

const StyledAddChart = styled(Addchart)`
  font-size: 20px;
  margin-bottom: 2px;
  color: #666666;
`;

const ChartContainer = styled.div`
  width: 100%;
  display: flex;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const ChartControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ToggleContainerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666666;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #999999;
  gap: 8px;
`;